import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from '@ibk/shared-translation';
import { buildBoletoFilename, resolveOrderIdentifier } from '../utils/fileNamingUtils';
import { copyPaymentCode, downloadBankSlip, downloadBankSlipPdf, generatePaymentCode, } from '../utils/rechargePaymentUtils';
import { useBenefitContract, useRecharge, useRechargeBankSlip, useRechargePaymentCode } from './index';
export function useRechargePaymentPage(config) {
    const { t } = useTranslation();
    const { rechargeId } = useParams();
    const [alertData, setAlertData] = useState({
        visible: false,
        message: '',
        type: 'success',
    });
    const [isAnnotationVisible, setIsAnnotationVisible] = useState(true);
    const { contractData, isLoading: isContractLoading } = useBenefitContract();
    const { recharge, isLoading: isRechargeLoading, isError: isRechargeError, } = useRecharge({
        voucherContractId: contractData?.id,
        rechargeId,
    });
    const { paymentCode, isLoading: isPaymentCodeLoading, isError: isPaymentCodeError, fetchPaymentCode, } = useRechargePaymentCode();
    const { fetchBankSlip, bankSlip, isLoading: isBankSlipLoading, isSuccess: isBankSlipSuccess, isError: isBankSlipError, } = useRechargeBankSlip();
    useEffect(() => {
        if (contractData?.id && rechargeId && recharge) {
            fetchPaymentCode({
                voucherContractId: contractData.id,
                voucherContractRechargeId: rechargeId,
            });
        }
    }, [contractData?.id, rechargeId, recharge]);
    const isLoading = isContractLoading || isRechargeLoading || isPaymentCodeLoading;
    const hasError = isRechargeError || isPaymentCodeError;
    const isDataReady = !isLoading && !hasError && Boolean(recharge) && Boolean(paymentCode);
    const translationKeys = useMemo(() => config.translationKeys, [config.translationKeys]);
    const companyName = contractData?.companyName ?? '';
    const accrualPeriod = recharge?.recharge?.accrualPeriod ?? '';
    const rechargeName = recharge?.recharge?.name;
    const orderIdForFile = recharge?.recharge?.orderId ?? '';
    useEffect(() => {
        if (isBankSlipSuccess && bankSlip?.result) {
            const filename = buildBoletoFilename(companyName, accrualPeriod, resolveOrderIdentifier(rechargeName, orderIdForFile));
            downloadBankSlipPdf(bankSlip.result, filename, setAlertData, t(translationKeys.pdfUnavailable));
        }
    }, [
        isBankSlipSuccess,
        bankSlip,
        t,
        translationKeys.pdfUnavailable,
        companyName,
        accrualPeriod,
        rechargeName,
        orderIdForFile,
    ]);
    useEffect(() => {
        if (hasError) {
            setAlertData({
                visible: true,
                message: t(translationKeys.paymentLoadingError),
                type: 'error',
            });
        }
    }, [hasError, t, translationKeys.paymentLoadingError]);
    useEffect(() => {
        if (isBankSlipError) {
            setAlertData({
                visible: true,
                message: t(translationKeys.pdfUnavailable),
                type: 'error',
            });
        }
    }, [isBankSlipError, t, translationKeys.pdfUnavailable]);
    const codeField = useMemo(() => config.codeField, [config.codeField]);
    const handleCopyCode = async () => {
        const codeValue = codeField === 'bankSlipSerialNumber' ? paymentCode?.bankSlipSerialNumber : paymentCode?.copyAndPaste;
        await copyPaymentCode({
            paymentCode: codeValue,
            setAlertData,
            t,
            translationKey: translationKeys.codeCopied,
        });
    };
    const handleGenerateCode = async () => {
        await generatePaymentCode({
            contractId: contractData?.id,
            rechargeId,
            recharge,
            fetchPaymentCode,
            setAlertData,
            t,
            codeField,
            generatingKey: translationKeys.generatingCode,
            generatedKey: translationKeys.codeGenerated,
            notGeneratedKey: translationKeys.codeNotGenerated,
        });
    };
    const handleDownloadBankslip = async () => {
        downloadBankSlip({
            contractId: contractData?.id,
            rechargeId,
            recharge,
            fetchBankSlip,
        });
    };
    const handleCloseAnnotation = () => {
        setIsAnnotationVisible(false);
    };
    return {
        alertData,
        recharge,
        paymentCode,
        isPaymentCodeLoading,
        isPaymentCodeError,
        isDataReady,
        isBankSlipLoading,
        isAnnotationVisible,
        setAlertData,
        handleGenerateCode,
        handleCopyCode,
        handleDownloadBankslip,
        handleCloseAnnotation,
    };
}
